import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FocusEvent, KeyboardEvent } from "react";
import { Box, Button, ClickAwayListener, Paper, Popover, Popper, useTheme } from "@material-ui/core";
import { useSnackbar } from "notistack5";
import useContactWrites from "hooks/contacts/useContactWrites";
import useCrmPanelContact from "../../panel/useCrmPanelContact";
import { describeCrmBulkFailure } from "../../bulk/crmBulkCopy";
import { useOutsideDismiss } from "hooks/useOutsideDismiss";
import {
  beginCrmCellSave,
  endCrmCellSave,
  isCrmCellSaveCurrent,
  takeCrmCellEditTarget
} from "../cells/crmCellEditing";
import {
  CellAmenities,
  CellAreas,
  CellFloors,
  CellMoney,
  CellMoneyRange,
  CellNotes,
  CellRooms,
  CellSizeRange,
  CellStatus,
  CellTags,
  CellText,
  CellToggles,
  controlMinWidth,
  controlNeedsModalSurface,
  originCaption,
  readEditorKey
} from "./CrmCellControls";
import {
  buildCellPatch,
  cellPatchIsEmpty,
  customerTypeOptions,
  distinctStoredBudgetFloor,
  isLocationDimension,
  propertyTypeOptions,
  seedCellDraft
} from "./crmCellEditModel";
import { describeCellDraftProblem } from "./crmCellValidation";
import { CONTACT_NAME_MAX_LENGTH } from "../../../../../utils/contacts/contactWriteLimits";
import { isContactNotInYourBookError, isWebotServerError } from "../../../../../utils/contacts/contactWriteErrors";
import { customerTypes as CUSTOMER_TYPES, propertyTypes as PROPERTY_TYPES } from "../../../../../@types/sharedSchema";
import type { SxProps } from "@material-ui/system";
import type { Theme } from "@material-ui/core/styles";
import type { AreasHydration } from "./CrmCellControls";
import type { CrmCellDraft, CrmCellSubject, CrmEditableDimension, LocationsBaseline } from "./crmCellEditModel";
import type { CrmCellEditTarget } from "../cells/crmCellEditing";
import type { CrmStatus, ProcessedListingAddress } from "../../../../../@types/sharedSchema";
import type { CrmContactRow } from "../../../../../@types/webotServer";

/**
 * IN-CELL EDITING — click a field in the table, edit it there.
 *
 * This replaces `CrmInlineEditCell`, which answered a cell click by opening the
 * multi-field quick-edit popover focused on that cell's dimension. That was rejected
 * verbatim: *"get rid of your 'quick edit' modal. this is not what i meant. i want to be
 * able to click on a field in the table, and edit it in place. click on name - i show a
 * text input. click on budget - becomes budget input (separators)."*
 *
 * So: one cell, one field, one control, and no route to it except clicking the cell.
 * `CrmCellControls.tsx` explains why each control is the control it is and why they render
 * on an anchored surface rather than literally inside a 56px column; `crmCellEditModel.ts`
 * holds every data rule about what a cell may write. This file is the LIFECYCLE — seed,
 * dismiss, save, fail.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * THIS EXPORT IS A CONTRACT. The row renders it; the row does not own it.
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * `CrmContactTableRow` swaps a cell's display content for `<CrmCellEditor/>` while that
 * cell is being edited, and knows nothing else about editing: not which control a
 * dimension gets, not which document field it writes, not how it saves, not what it does
 * when the save fails. Everything below the `CrmCellEditorProps` boundary is free to
 * change without touching the row.
 *
 * ⚠️ THERE ARE TWO HOSTS NOW, and the contract is written for both. The second is the
 * contact page's סקירה tab (`overview/edit/InlineFieldEdit.tsx`), which anchors this to a
 * chip rather than to a cell. What that host does differently, and why, in one place:
 *
 *   - it passes `anchorSx`, because the default `height: 100%` computes to ZERO against a
 *     chip row's auto height and would open the surface at the trigger's top edge;
 *   - it attaches `subject.doc`, which unlocks the seven `CRM_DOC_DIMENSIONS`;
 *   - it passes NO `onPatched`, because it renders from a live Firestore listener that
 *     repaints on its own — so there is no optimistic overlay to apply and none to revert;
 *   - it announces NO cell, so `cell === null` and the save runs unmarked. That is a
 *     supported mode, not a degradation: the spinner and the re-open lock it disables are
 *     `CrmEditableCell`'s, and a chip has neither.
 *
 * The HOST's side of the contract is exactly three things:
 *   1. render this where the editor should anchor, sized so the anchor matches the trigger;
 *   2. unmount it when `onDone` fires — this component does NOT close itself;
 *   3. hand `onPatched` a row-patch channel IF it has one (`useCrmRowLocalPatch.applyPatch`
 *      on the list; nothing on the contact page).
 *
 * `onDone` deliberately does not say whether the edit saved or was cancelled. The host's
 * response is identical either way — unmount — and a `saved: boolean` would invite the row
 * to do something different in the two cases, which is how a "did it save?" spinner ends
 * up in a component that has no idea. The spinner that DOES exist is `CrmEditableCell`'s,
 * and it is driven by the `crmCellEditing` registry rather than by a prop, precisely so the
 * row stays out of it.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * SAVING: blur is the primary path, and the write does not hold the cell
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * "save on blur and on Enter; cancel on Escape", for a fill-then-move-on flow — so the
 * commit fires and the editor closes IMMEDIATELY, without waiting for the round trip. The
 * row repaints from the optimistic patch, and the realtor is already typing in the next
 * cell while the request is in flight.
 *
 * That is the opposite of the quick-edit popover's choice, which stayed open until the
 * write settled, and the reason it could is that the popover had nowhere to report a late
 * failure to. This one does — twice over:
 *
 *  - THE CELL, for as long as the write is in the air. `beginCrmCellSave` marks the cell
 *    the moment the request leaves and `endCrmCellSave` clears it when it lands, and
 *    `CrmEditableCell` renders a scrim + spinner over the value meanwhile and refuses to
 *    re-open. So "the editor closed" no longer silently means "it saved". The registry is
 *    the only channel available: the editor is unmounted long before the answer arrives,
 *    so nothing it returns or calls back with could still reach the cell.
 *  - `notistack`, for a failure. It is mounted app-wide (above the router in `App.tsx`), so
 *    a failure that lands after the editor is gone — or after the whole LIST is gone —
 *    still reverts the optimistic patch and raises a snackbar carrying a **retry button**.
 *    The retry closes over the patch, so the realtor's typing is recoverable in one click
 *    rather than re-typed. Reverting silently would leave the row showing the old value
 *    with no explanation, which is exactly the "save that silently does nothing" this
 *    whole path exists to prevent.
 *
 * NOTHING IS SENT WHEN NOTHING CHANGED. `buildCellPatch` compares every dimension against
 * the row and emits no key when they agree, and an empty patch short-circuits before the
 * request. This is not an optimisation: six Cloud Functions fire on a contact write, and
 * with blur-to-save the no-op is the COMMON case — a realtor who clicks a cell, reads it
 * and clicks away has changed nothing, and does that far more often than they type. It is
 * also what keeps the spinner meaningful: a cell that flashed "saving" every time it was
 * merely looked at would be noise, and the reader would stop believing it.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 * TWO SAVES ON ONE CELL: PREVENTED first, last-write-wins as the backstop
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * The primary answer is PREVENTION, and it is structural rather than a check: while a
 * cell's save is in flight the cell will not open its editor at all, so the ordinary route
 * to a second write on the same cell does not exist. That is the right primary because it
 * is the only one the realtor can SEE — a racing pair that the code silently resolved
 * would still have shown them two edits and kept one.
 *
 * Three paths get past the lock, none of them by a click:
 *
 *  - the RETRY button, pressed after a failure has already unlocked the cell;
 *  - the unmount flush below, which can fire without a commit;
 *  - the identity column's note editor, which is opened from `CrmIdentityCell` and
 *    announces no cell at all, so it is outside the registry entirely (`cell === null`).
 *
 * For those, the backstop is LAST-WRITE-WINS, enforced on the CALLBACKS rather than on the
 * requests: every attempt quotes its number back through `isCrmCellSaveCurrent`, and an
 * attempt that is no longer the newest one for its cell touches nothing when it lands. The
 * requests themselves are never cancelled — see `send` — so the newest one still reaches
 * the server, and the server is the tie-breaker it should be. What this prevents is the
 * genuinely destructive case: an older attempt failing and reverting the row on top of a
 * newer value that SUCCEEDED, which would make a completed save visibly disappear.
 */

// ───────────────────────────────────────────────────────────────────────────────
// The contract
// ───────────────────────────────────────────────────────────────────────────────

export type { CrmEditableDimension } from "./crmCellEditModel";
export { isCrmEditableDimension, CRM_EDITABLE_DIMENSIONS } from "./crmCellEditModel";

export type CrmCellEditorProps = {
  /**
   * What is being edited. A mirror ROW is the whole story for the table; a host that also
   * holds the contact DOCUMENT attaches it as `doc` and unlocks `CRM_DOC_DIMENSIONS`.
   *
   * Still named `row` because `CrmContactRow` is assignable to `CrmCellSubject` and every
   * existing call site passes exactly a row — renaming the prop would have churned the
   * table for nothing.
   */
  row: CrmCellSubject;
  dimension: CrmEditableDimension;
  /** Called when the editor is finished — saved OR cancelled. The host unmounts it. */
  onDone: () => void;
  /** Optimistically patch the row in the list. Same channel the panel already uses. */
  onPatched?: (patch: Partial<CrmContactRow>) => void;
  /**
   * How the invisible anchor sizes itself, for hosts whose trigger is not a table cell.
   *
   * ⚠️ THE DEFAULT ONLY WORKS INSIDE A CELL. `width/height: 100%` needs a parent with a
   * definite height, which a table cell has and a chip in a wrapping row does not — there
   * it computes to ZERO and the surface opens at the trigger's top edge instead of under
   * it. The width matters too: the host takes `max(controlMinWidth, anchor.offsetWidth)`,
   * so an anchor that accidentally measured the whole 352px reference column would open a
   * 352px popover for a 60px chip.
   *
   * A panel host therefore wraps its trigger in `position: relative` and passes an
   * absolutely-positioned `inset: 0` overlay, which has the trigger's exact rect in both
   * axes. `pointerEvents: "none"` on it is safe — the surface is portalled and dismissal
   * tests the PAPER, never the anchor.
   */
  anchorSx?: SxProps<Theme>;
};

// ───────────────────────────────────────────────────────────────────────────────

export function CrmCellEditor({ row, dimension, onDone, onPatched, anchorSx }: CrmCellEditorProps): JSX.Element {
  /**
   * The anchor as STATE, not a ref.
   *
   * A ref would be `null` on the first render, and nothing would schedule the second one
   * that finally has an element to anchor to — the surface would simply never open. The
   * callback-ref-into-state form is what makes the mount itself the trigger.
   */
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const theme = useTheme();

  /**
   * WHICH CELL THIS EDITOR IS SAVING ON BEHALF OF — read ONCE, at mount, from whatever the
   * cell announced on the click that opened us.
   *
   * A lazy ref rather than `useState(take…)`: the announcement is consumed on read, so it
   * must be read exactly once per editor. A state initialiser can be invoked more than
   * once for a single mount, and the second call would consume a `null` and overwrite a
   * real target with it.
   *
   * `null` is a legitimate answer, not a failure — the identity column's note trigger
   * opens this same editor without announcing (see `crmCellEditing`), and that save
   * simply runs unmarked, exactly as every save did before the spinner existed. Every
   * registry call below is therefore guarded on it rather than assuming it is there.
   */
  const targetRef = useRef<{ cell: CrmCellEditTarget | null } | null>(null);
  if (!targetRef.current) targetRef.current = { cell: takeCrmCellEditTarget() };
  const cell = targetRef.current.cell;

  const owner = row.ownerUserId || null;

  /**
   * The write goes through `useContactWrites`, like every other contact write — it is the
   * one module that also tells the local stores, so a name fixed in this cell is at once the
   * name the calls table and the pickers show, instead of whenever their copy next goes stale.
   *
   * Whose book is being written is the hook's decision, from the owner named in `send`:
   * `view: "org"` is sent ONLY for a row that belongs to somebody else, because the
   * server's default `mine` scope would 404 a colleague's contact — and because naming the
   * org view unnecessarily is how a `CRM_SCOPE_AMBIGUOUS` 400 reaches a caller who belongs
   * to two agencies and had no reason to be asked. The server INTERSECTS a named owner with
   * the caller's resolved scope, so naming one can never reach outside it.
   *
   * Its functions are referentially stable, so the save callback is not re-created — and
   * its effects not re-fired — by unrelated app-shell renders.
   */
  const { updateContact } = useContactWrites();

  const isLocation = isLocationDimension(dimension);
  const isNotes = dimension === "additionalNotes";
  const modalSurface = controlNeedsModalSurface(dimension);

  // ── the draft ───────────────────────────────────────────────────────────────
  //
  // MOUNT-SEEDED, and that makes the host's unmount-on-`onDone` contract load-bearing: an
  // editor kept mounted while `row` changed underneath it would keep the previous
  // contact's numbers on screen and write them onto the wrong record. `ScaleRangeStepper`
  // and the location picker copy their seeds internally too, so this is not the only copy
  // that would go stale — which is why the contract says unmount rather than "pass a new
  // row".
  const [draft, setDraft] = useState<CrmCellDraft>(() => seedCellDraft(row, dimension));

  /**
   * The draft as it was SEEDED, held for the validator's unchanged-value exemption.
   *
   * A ref rather than state: it is written once and never re-read by render, and it must
   * not move when the row does — the whole point is that it records what the realtor was
   * SHOWN, so a legacy-invalid stored value can be looked at and dismissed without the
   * editor complaining about something they did not type.
   */
  const seedRef = useRef<CrmCellDraft | null>(null);
  if (!seedRef.current) seedRef.current = seedCellDraft(row, dimension);

  // ── the document, for the areas control only ────────────────────────────────
  //
  // `null` for every other dimension, which makes the hook a complete no-op — no listener
  // opened, no request issued. The areas control is the ONE field whose current value the
  // row cannot express (see `CellAreas`), so it is the only one that pays for a read.
  const hydration = useCrmPanelContact(isLocation ? row.contactId : null, owner);

  const [baseline, setBaseline] = useState<LocationsBaseline>({ status: "pending" });

  /**
   * WHAT `commit` READS — and the draft half of it is written SYNCHRONOUSLY, never taken
   * from the render.
   *
   * ⚠️ THIS IS A CORRECTNESS FIX, NOT A CACHE. It used to be `liveRef.current = { draft,
   * baseline }` on every render, which loses a value whenever a control lifts its own state
   * in the SAME event dispatch that commits. That is not hypothetical — it is the rooms
   * picker, reported: `ScaleStepper` keeps the typed number in its own state and lifts it
   * on BLUR, so clicking away fires the input's blur (which calls `onChange` → `setDraft`,
   * batched) and then the dismissal (which reads this ref) in one dispatch, before React
   * has re-rendered. The number the realtor typed was simply gone, and the workaround they
   * found — click something else inside the picker first, then click away — is exactly
   * "give React a separate dispatch to flush in".
   *
   * Writing the ref inside `applyDraft` closes it for every path at once: blur-then-commit,
   * Enter, the unmount flush, and a picker that commits an override in the same tick it
   * sets state. `baseline` still rides the render, because nothing lifts it mid-dispatch.
   */
  const liveRef = useRef({ draft, baseline });
  liveRef.current.baseline = baseline;

  /**
   * The ONLY way the draft changes. Sets the ref first, then the state from the same value.
   *
   * Not `setDraft(previous => …)`: the ref is already the newest draft by construction, so
   * a functional update would be reading a staler source than the one right here.
   */
  const applyDraft = useCallback((patch: Partial<CrmCellDraft>) => {
    const next = { ...liveRef.current.draft, ...patch };
    liveRef.current.draft = next;
    setDraft(next);
    // A COMPLAINT DOES NOT SURVIVE THE KEYSTROKE THAT ANSWERS IT. Without this the field
    // stays red — still naming the old problem — while the realtor corrects it, so the
    // one signal that says "this is wrong" goes on saying it about a value that is now
    // right. Cleared optimistically rather than re-validated on every character: the
    // dismissal re-checks anyway, and a message that reappears mid-word is worse than
    // one that waits.
    setProblem((current) => (current === null ? current : null));
  }, []);

  /**
   * Seed the areas EXACTLY ONCE, when the document first arrives.
   *
   * The own-contact branch of the hydrate is a live `onSnapshot`, so it fires again for
   * every later change — including the echo of our own write. Re-seeding on those would
   * overwrite whatever has been picked since. A ref rather than state because it must be
   * read and set inside one effect run, before any re-render can observe it.
   */
  const areasSeeded = useRef(false);
  useEffect(() => {
    if (!isLocation || areasSeeded.current || !hydration.contact) return;
    areasSeeded.current = true;
    const stored = Array.isArray(hydration.contact.interestedInLocations)
      ? (hydration.contact.interestedInLocations as ProcessedListingAddress[])
      : [];
    // The SAME array instance goes into the draft and into the baseline, so the first
    // comparison is trivially equal and `LocationPicker`'s mount-time `onLocationsChange`
    // emit — it fires once with whatever it was seeded with — cannot register as an edit.
    applyDraft({ locations: stored });
    setBaseline({ status: "ready", locations: stored });
  }, [hydration.contact, isLocation, applyDraft]);

  const areasHydration: AreasHydration = useMemo(() => {
    if (baseline.status === "ready") return { status: "ready" };
    if (hydration.error) return { status: "unavailable", reason: "error" };
    if (hydration.missing) return { status: "unavailable", reason: "missing" };
    return { status: "loading" };
  }, [baseline.status, hydration.error, hydration.missing]);

  // ── the save ────────────────────────────────────────────────────────────────


  /**
   * Set the moment a commit or a cancel is decided.
   *
   * Three independent things can dismiss this editor — a click away, focus leaving, a key
   * — and on a fast interaction two of them fire for one gesture (clicking the next cell
   * both blurs the input and lands outside the paper). Without this, that gesture would
   * issue the same write twice.
   */
  const settledRef = useRef(false);

  /**
   * Issue the write, and own its failure.
   *
   * Deliberately NOT aborted when this component goes away: the write is what the realtor
   * asked for, and cancelling it to tidy up a component would be the data loss this file
   * exists to avoid. `onPatched` targets the LIST's state and the save marker lives in a
   * module registry, so both outlive the editor and both are safe to touch from an
   * unmounted closure.
   *
   * ── What happens when the user leaves mid-save ──────────────────────────────
   *
   * Opening the SIDE PANEL changes nothing: the panel is a sibling of the list, the row
   * stays mounted, the patch lands and the spinner clears where the realtor can see it.
   *
   * Leaving the PAGE unmounts the whole list, and three things then matter, in order of
   * how much they matter:
   *
   *  1. THE WRITE STILL COMPLETES. It is a real request that was already sent; the server
   *     neither knows nor cares that a component went away.
   *  2. THE FAILURE IS STILL REPORTED. `SnackbarProvider` sits above `<Router/>` in
   *     `App.tsx`, so the toast — and its retry — survive the navigation and appear on
   *     whatever page the realtor landed on. This is the reason the failure path was built
   *     on a snackbar rather than on anything owned by the list.
   *  3. `onPatched` becomes a write to unmounted state. React 17 logs a development-only
   *     warning for that and does nothing else; there is no corrupt state to be had,
   *     because the list's state died with it. Guarding it would mean either abandoning
   *     the write (see above) or teaching this component a mounted-flag it would then have
   *     to keep honest across three exit paths, to suppress a dev-only log line.
   *
   * The save MARKER is the one thing that genuinely outlives everything, because the
   * registry is module state. That is deliberate — come back to the CRM within the second
   * or so the request takes and the cell is still correctly marked saving — and it is
   * exactly why `endCrmCellSave` is called on BOTH branches below and never conditionally.
   * A marker left behind by an early return is a cell that spins until a full reload.
   */
  const send = useCallback(
    (fields: Record<string, unknown>, rowPatch: Partial<CrmContactRow>, previous: Partial<CrmContactRow>) => {
      const cellKey = cell?.key ?? null;

      const attempt = (): void => {
        // Applied by EVERY attempt, not once before the first. A retry runs after the
        // failure has already reverted the row, so re-applying is what makes the retry
        // visible at all — and what puts the cell back under its spinner instead of
        // leaving a second write in flight behind a cell that looks idle.
        onPatched?.(rowPatch);

        const attemptId = cellKey ? beginCrmCellSave(cellKey) : 0;

        /**
         * Has anything been written to this cell since we left? An unmarked save
         * (`cellKey === null`) has no way to be superseded and is always current, which
         * keeps the name/note path behaving exactly as it did before the registry existed.
         */
        const isNewest = (): boolean => !cellKey || isCrmCellSaveCurrent(cellKey, attemptId);
        const settle = (): void => {
          if (cellKey) endCrmCellSave(cellKey);
        };

        void updateContact(row.contactId, fields, row.ownerUserId ? { ownerUserId: row.ownerUserId } : undefined)
          .then((response) => {
            // Unlock FIRST, so the cell is clickable again in the same tick its value
            // settles. A realtor who wants to correct what they just typed is reaching for
            // that cell already.
            settle();
            // The PERSISTED row beats our projection whenever the mirror caught up — it is
            // read back after the inline drain rather than echoed from the patch. The
            // mirror has been fleet-wide since 2026-08-25, so it is null only when that
            // drain failed and the scheduled one has yet to repair it; the optimistic patch
            // covers that minute. The response also carries the whole document (`doc` +
            // `version`), which `useContactWrites` has already written into the document
            // store by now — an open panel on this contact repainted in the same tick.
            // (No response at all is the hook's "nothing to save" — an empty patch is a no-op.)
            if (isNewest() && response && response.contact) onPatched?.(response.contact);
          })
          .catch((caught: unknown) => {
            settle();

            /**
             * REVERT — key by key, and only if we are still the last word on this cell.
             *
             * Key by key rather than a whole stale row, so the revert cannot clobber a
             * field some other control (the status pill, the 🔥) changed while this
             * request was in flight. Newest-only, so a failure that lands after the
             * realtor has already re-saved this cell successfully does not drag their
             * good value back to the pre-edit one.
             *
             * ⚠️ KNOWN AND DELIBERATE: `previous` is the value the row held at COMMIT
             * time, and there is no way from here to ask what it holds NOW — this closure
             * outlives the component that had the row. So if the list re-fetched this
             * contact mid-flight and brought a DIFFERENT value for the same key (a
             * colleague's edit, a mirror drain catching up), `useCrmRowLocalPatch` will
             * already have dropped our optimistic key in favour of it, and this revert
             * re-introduces a value nobody holds any more. It is a stale DISPLAY, not a
             * write — the save failed, so nothing was persisted either way — and it
             * survives only until the next list update touches that key. Fixing it
             * properly means a "clear this key" verb on the row-patch channel, so that a
             * failure can withdraw the overlay entry instead of overwriting it with
             * history; that verb lives in `useCrmRowLocalPatch`, which this change does
             * not own. Reverting to the WRONG old value would be the worse bug, and the
             * newest-only guard above is what keeps that to the one case that cannot be
             * seen from here.
             */
            if (isNewest()) onPatched?.(previous);

            const message = describeCellSaveFailure(caught, cell?.label ?? null, row.name);

            // The retry closes over `fields`, so the realtor's typing survives the editor
            // being gone. Without it a failed save is unrecoverable except by re-typing —
            // and the row has already reverted, so there is nothing left on screen to
            // re-type FROM.
            enqueueSnackbar(message, {
              variant: "error",
              autoHideDuration: 10_000,
              action: (key) => (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    closeSnackbar(key);
                    // A toast lives for ten seconds, which is long enough for the realtor
                    // to fix the cell by hand and then press the button out of habit.
                    // Re-sending these bytes would silently undo the value they just got
                    // right, so the stale retry declines and says so instead.
                    if (!isNewest()) {
                      enqueueSnackbar("הערך כבר עודכן מאז — פתחו את התא שוב אם צריך.", {
                        variant: "info",
                        autoHideDuration: 6_000
                      });
                      return;
                    }
                    attempt();
                  }}
                >
                  נסו שוב
                </Button>
              )
            });
            // eslint-disable-next-line no-console
            console.error("CrmCellEditor: save failed", { dimension, fields }, caught);
          });
      };

      attempt();
    },
    [cell, closeSnackbar, dimension, enqueueSnackbar, onPatched, row.contactId, row.name, row.ownerUserId, updateContact]
  );

  // The unmount flush below installs its cleanup ONCE (see its dep list), so it would
  // otherwise close over the first `send` — and with it the first `onPatched`, which the
  // row re-creates on every render. The ref keeps the flush writing to the CURRENT list.
  const sendRef = useRef(send);
  sendRef.current = send;

  /**
   * Finish: build the patch, send it if there is one, and hand the cell back.
   *
   * `override` exists for the pickers, which have no blur to wait for — `CellStatus`
   * commits the value it was just clicked with, in the same tick it sets it, so it cannot
   * read that value back out of state yet.
   *
   * The ORDER matters: `send` marks the cell saving synchronously, and only then does
   * `onDone` hand the cell back. So the cell mounts with the marker already set and reads
   * it in its state initialiser — there is no frame in which the cell is on screen looking
   * idle over a write that has already left.
   */
  const commit = useCallback(
    (override?: Partial<CrmCellDraft>) => {
      if (settledRef.current) return;
      settledRef.current = true;

      const effective = override ? { ...liveRef.current.draft, ...override } : liveRef.current.draft;
      const patch = buildCellPatch(row, dimension, effective, liveRef.current.baseline);

      if (!cellPatchIsEmpty(patch)) {
        const previous: Partial<CrmContactRow> = {};
        (Object.keys(patch.rowPatch) as string[]).forEach((key) => {
          (previous as Record<string, unknown>)[key] = (row as unknown as Record<string, unknown>)[key];
        });
        send(patch.fields, patch.rowPatch, previous);
      }

      onDone();
    },
    [dimension, onDone, row, send]
  );

  /** Escape: drop the draft on the floor. The row still shows the value it always did. */
  const cancel = useCallback(() => {
    if (settledRef.current) return;
    settledRef.current = true;
    onDone();
  }, [onDone]);

  /**
   * ── REFUSE ONCE, THEN DISCARD ─────────────────────────────────────────────────
   *
   * The dismissal path for the two dimensions that can be objectively wrong (`mail`,
   * `tehudatZehut`). Everything else routes straight to `commit` and is byte-identical to
   * what it always did.
   *
   *   invalid, first dismissal  → SHOW the complaint, stay open, save nothing
   *   invalid, second dismissal → CANCEL, discarding the bad value
   *   valid                     → commit, exactly as before
   *
   * ⚠️ THIS WRAPS `commit`; IT IS NOT INSIDE IT. `commit` sets `settledRef` before it does
   * anything else, so an early return placed in there would make every LATER dismissal a
   * no-op and leave the popup unclosable — and the unmount flush, which never calls
   * `commit` at all, would persist the invalid value regardless. The guard therefore sits
   * on the callers, and the flush consults the same predicate independently.
   *
   * The second dismissal cancels rather than refusing again, because the alternative is a
   * surface the realtor cannot leave without fixing a field they may have opened by
   * accident. Two clicks always get out.
   */
  const refusedOnceRef = useRef(false);
  const [problem, setProblem] = useState<string | null>(null);

  const dismiss = useCallback(() => {
    if (settledRef.current) return;
    const complaint = describeCellDraftProblem(dimension, liveRef.current.draft, seedRef.current!);
    if (complaint) {
      if (!refusedOnceRef.current) {
        refusedOnceRef.current = true;
        setProblem(complaint);
        return;
      }
      cancel();
      return;
    }
    // Cleared on the way through, so fixing the value and dismissing again behaves like a
    // first dismissal would have — the refusal is about the VALUE, not a mode the editor
    // is now stuck in.
    refusedOnceRef.current = false;
    setProblem(null);
    commit();
  }, [cancel, commit, dimension]);

  /**
   * BEST-EFFORT FLUSH ON UNMOUNT.
   *
   * Every deliberate exit already commits, but this component does not control its own
   * lifetime: the parent can tear it down without dismissing it (a route change, a list
   * refetch, the row leaving the page). Without this, a draft in that moment is lost
   * silently — the hardest failure to notice, because nothing on screen ever said a save
   * was pending.
   *
   * It marks the cell saving like any other write, which is worth stating because it is
   * the one path whose spinner can outlive the list that would have shown it: flush on a
   * route change and the marker is set with nobody subscribed, then cleared when the
   * request lands. That is harmless, and it is what makes coming straight back show a
   * still-saving cell rather than a suspiciously idle one.
   *
   * `settledRef` is what keeps the ordinary "committed, then closed" path from
   * double-sending, and the empty dep list installs the cleanup once rather than rebuilding
   * it on every keystroke.
   */
  useEffect(
    () => () => {
      if (settledRef.current) return;
      settledRef.current = true;
      /**
       * ⚠️ THE FLUSH CONSULTS THE VALIDATOR INDEPENDENTLY, and it has to.
       *
       * This path never calls `commit`, so the refuse-once wrapper above cannot reach it.
       * Without this line, an invalid email that the surface had already refused would be
       * persisted anyway the moment the parent tore the editor down — a route change, a
       * list refetch, a contact switch — which is the one exit the realtor cannot see and
       * therefore the worst place to write a value they were just told was wrong.
       *
       * Dropping it is the right outcome, not a compromise: a flush is a best-effort
       * rescue of a draft nobody dismissed, and a draft that would have been refused on
       * dismissal has no better claim to be saved just because the component went away.
       */
      if (describeCellDraftProblem(dimension, liveRef.current.draft, seedRef.current!)) return;
      const pending = buildCellPatch(row, dimension, liveRef.current.draft, liveRef.current.baseline);
      if (cellPatchIsEmpty(pending)) return;
      const previous: Partial<CrmContactRow> = {};
      (Object.keys(pending.rowPatch) as string[]).forEach((key) => {
        (previous as Record<string, unknown>)[key] = (row as unknown as Record<string, unknown>)[key];
      });
      sendRef.current(pending.fields, pending.rowPatch, previous);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // ── dismissal ───────────────────────────────────────────────────────────────

  /**
   * The click-away listener is ARMED ONE TICK LATE, and it has to be.
   *
   * The click that opened this editor is still propagating toward `document` when React
   * commits the mount — and a listener added to a node during propagation IS invoked if the
   * event has not reached that node yet. So an immediately-live `ClickAwayListener` reads
   * the OPENING click as a click away and closes the editor in the same gesture that
   * requested it. A macrotask is enough to let that event finish.
   */
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    const handle = setTimeout(() => setArmed(true), 0);
    return () => clearTimeout(handle);
  }, []);

  /**
   * THE DISMISSING CLICK IS SWALLOWED — it closes this editor and does nothing else.
   *
   * See `hooks/useOutsideDismiss.ts`: this reverses the original "no backdrop, so the same click
   * opens the next cell" behaviour, which in use read as an editor that could not be put
   * away. It applies to the non-modal surface ONLY, because the modal one already has a
   * backdrop that has always done exactly this — that inconsistency is half the reason
   * this exists.
   *
   * The surface is PORTALLED, so containment is tested against the Paper rather than
   * against the anchor sitting in the cell. Clicking the cell this editor belongs to is
   * therefore "outside", which is right: there is nothing left in that cell to click.
   */
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const isInsideSurface = useCallback((node: Node) => Boolean(surfaceRef.current?.contains(node)), []);
  useOutsideDismiss({
    enabled: armed && !modalSurface && Boolean(anchorEl),
    isInside: isInsideSurface,
    onDismiss: dismiss
  });

  /**
   * Focus leaving the editor is a blur — but ONLY when it went somewhere.
   *
   * A `relatedTarget` of `null` means focus went nowhere identifiable, which is what a
   * click on a non-focusable region produces; committing on that would tear the editor down
   * when the realtor clicked its own padding. The click-away listener is the correct
   * handler for that case, and it runs a moment later.
   */
  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget as Node | null;
    if (!next || event.currentTarget.contains(next)) return;
    dismiss();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const action = readEditorKey(event, isNotes);
    if (!action) return;
    event.preventDefault();
    event.stopPropagation();
    if (action === "commit") dismiss();
    else cancel();
  };

  // ── the control ─────────────────────────────────────────────────────────────

  /** Every control's setter. See `applyDraft` for why it does not touch state directly. */
  const patchDraft = applyDraft;

  const control = (() => {
    switch (dimension) {
      case "name":
        return (
          <CellText
            value={draft.text}
            placeholder="שם הלקוח"
            maxLength={CONTACT_NAME_MAX_LENGTH}
            onChange={(text) => patchDraft({ text })}
          />
        );

      case "contactOrigin":
        return (
          <CellText
            value={draft.text}
            placeholder="מקור הליד"
            caption={originCaption(draft.text)}
            onChange={(text) => patchDraft({ text })}
          />
        );

      case "additionalNotes":
        return <CellNotes value={draft.text} onChange={(text) => patchDraft({ text })} />;

      case "budget":
        return (
          <CellMoney
            value={draft.amount}
            storedFloor={distinctStoredBudgetFloor(row)}
            onChange={(amount) => patchDraft({ amount })}
          />
        );

      case "budgetRent":
        return (
          <CellMoney
            value={draft.amount}
            storedFloor={null}
            hint="תקציב שכירות חודשי"
            onChange={(amount) => patchDraft({ amount })}
          />
        );

      case "rooms":
        return (
          <CellRooms
            min={draft.roomsMin}
            max={draft.roomsMax}
            onChange={(roomsMin, roomsMax) => patchDraft({ roomsMin, roomsMax })}
          />
        );

      case "crmStatus":
        return (
          <CellStatus
            row={row}
            status={draft.status}
            subStatus={draft.subStatus}
            // A pick IS the commit — see `CellStatus`. The value is handed to `commit`
            // directly because `setDraft` has not flushed yet in this tick.
            onPick={(status: CrmStatus | null, subStatus: string | null) => commit({ status, subStatus })}
          />
        );

      /**
       * ONE CLICK IS THE WHOLE INTERACTION: toggle, save, close.
       *
       * Requested as "when picking a customer type, close the dropdown and apply changes
       * (usually, we have only 1 customer type)", and the measured corpus agrees — of the
       * 20% of contacts that carry any type at all, nearly all carry exactly one. Making
       * the common case cost a click plus a dismissal was the complaint.
       *
       * It stays a TOGGLE rather than becoming single-select, which is what keeps multiple
       * types reachable without a hidden modifier: clicking an unselected type adds it and
       * closes, clicking a selected one removes it and closes. Every click is one complete
       * change, so a second type is "reopen, click" and a mistake is "reopen, click" —
       * predictable in both directions, where a replace-on-click would silently drop the
       * type the realtor had already set.
       *
       * `commit(override)` rather than `patchDraft` then commit: the draft setter is
       * async, so committing on the next line would write the PREVIOUS value. The status
       * picker directly below does the same thing for the same reason.
       */
      case "customerTypes":
        return (
          <CellToggles
            options={customerTypeOptions(row)}
            canonical={CUSTOMER_TYPES}
            value={draft.values}
            onChange={(values) => commit({ values })}
          />
        );

      case "propertyTypes":
        return (
          <CellToggles
            options={propertyTypeOptions(row)}
            canonical={PROPERTY_TYPES}
            value={draft.values}
            onChange={(values) => patchDraft({ values })}
          />
        );

      case "tags":
        return <CellTags value={draft.values} onChange={(values) => patchDraft({ values })} />;

      case "cities":
      case "neighborhoods":
        return (
          <CellAreas
            hydration={areasHydration}
            locations={draft.locations}
            onChange={(locations) => patchDraft({ locations })}
          />
        );

      /**
       * ── The document-backed dimensions ────────────────────────────────────────
       *
       * Offered only by a host that attached `subject.doc` — see `CRM_DOC_DIMENSIONS`.
       * Nothing here needs a guard for a missing document: the model refuses to emit
       * their keys without one, so the worst a mis-wired host can produce is a control
       * seeded blank that saves nothing.
       */
      case "mail":
        return (
          <CellText
            value={draft.text}
            placeholder="name@example.com"
            dir="ltr"
            inputMode="email"
            // The server's own `MAX_NAME_LENGTH`. Stopping the keystroke is the only
            // version of this bound the realtor can act on — a 400 after typing tells
            // them nothing and loses the text.
            maxLength={CONTACT_NAME_MAX_LENGTH}
            error={problem}
            onChange={(text) => patchDraft({ text })}
          />
        );

      case "address":
        // Free text, and Hebrew far more often than not — so no `dir`, which would pin an
        // address the realtor typed in Hebrew to the wrong reading order.
        return (
          <CellText
            value={draft.text}
            placeholder="רחוב, מספר, עיר"
            maxLength={CONTACT_NAME_MAX_LENGTH}
            onChange={(text) => patchDraft({ text })}
          />
        );

      case "tehudatZehut":
        return (
          <CellText
            value={draft.text}
            placeholder="123456789"
            dir="ltr"
            inputMode="numeric"
            maxLength={CONTACT_NAME_MAX_LENGTH}
            error={problem}
            caption="משמש למילוי אוטומטי של מסמכים לחתימה"
            onChange={(text) => patchDraft({ text })}
          />
        );

      case "floors":
        return (
          <CellFloors
            min={draft.floorsMin}
            max={draft.floorsMax}
            onChange={(floorsMin, floorsMax) => patchDraft({ floorsMin, floorsMax })}
          />
        );

      case "size":
        return (
          <CellSizeRange
            min={draft.amountMin}
            max={draft.amount}
            onChange={(amountMin, amount) => patchDraft({ amountMin, amount })}
          />
        );

      case "amenities":
        return <CellAmenities value={draft.amenities} onChange={(amenities) => patchDraft({ amenities })} />;

      case "budgetRange":
        return (
          <CellMoneyRange
            min={draft.amountMin}
            max={draft.amount}
            onChange={(amountMin, amount) => patchDraft({ amountMin, amount })}
          />
        );

      case "budgetRentRange":
        return (
          <CellMoneyRange
            min={draft.amountMin}
            max={draft.amount}
            hint="תקציב שכירות חודשי — מינימום ריק הוא טווח פתוח"
            onChange={(amountMin, amount) => patchDraft({ amountMin, amount })}
          />
        );

      default: {
        const never: never = dimension;
        return never;
      }
    }
  })();

  /**
   * Widen to the control's need, never narrow below the cell's own width.
   *
   * `offsetWidth` is read at render rather than measured in an effect: the anchor is
   * mounted in the same commit, so the value is available immediately and a
   * `ResizeObserver` here would only add a frame of the surface being the wrong width.
   */
  const cellWidth = anchorEl?.offsetWidth ?? 0;
  const surfaceWidth = Math.max(controlMinWidth(dimension), cellWidth);

  /** The READING-start edge — right in Hebrew. See the `Popover` origins below. */
  const startEdge: "left" | "right" = theme.direction === "rtl" ? "right" : "left";

  const body = (
    <Box onKeyDown={handleKeyDown} sx={{ width: surfaceWidth, maxWidth: "calc(100vw - 24px)", p: 1 }}>
      {control}
    </Box>
  );

  return (
    <>
      {/* The anchor. It fills the cell so the surface opens exactly over the value that was
          clicked, and it renders nothing — the cell's display content is what this editor
          replaced. */}
      <Box ref={setAnchorEl} sx={anchorSx ?? { width: "100%", height: "100%", minWidth: 0 }} />

      {anchorEl &&
        (modalSurface ? (
          /**
           * MODAL surface — for the controls that portal a listbox to `document.body`
           * (locations, tags). See the portal rule in `CrmCellControls.tsx`: a
           * `ClickAwayListener` would read a click on a suggestion as "outside" and
           * close-and-save before the pick registered.
           *
           * Escape and the backdrop both arrive at `onClose`, and they mean OPPOSITE
           * things — `reason` is what separates "I clicked away, save it" from "I pressed
           * Escape, forget it". Collapsing the two would make Escape save, which is the
           * one thing the requirement says it must not do.
           */
          <Popover
            open
            anchorEl={anchorEl}
            onClose={(_event, reason) => (reason === "escapeKeyDown" ? cancel() : dismiss())}
            /**
             * `startEdge`, not a hardcoded `"left"`.
             *
             * `Popover`'s origins are PHYSICAL and it does not flip them for RTL — unlike
             * `Popper`, whose `-start`/`-end` placements MUI swaps through `flipPlacement`
             * against `theme.direction`, which is why the branch below can say
             * `bottom-start` and be correct in both. These controls are 320px wide against
             * a 68-72px column, so anchoring to the wrong edge does not merely mis-align
             * the surface — it grows it away from the cell that was clicked, across the
             * columns the realtor was reading.
             */
            anchorOrigin={{ vertical: "bottom", horizontal: startEdge }}
            transformOrigin={{ vertical: "top", horizontal: startEdge }}
            PaperProps={{ sx: { borderRadius: 1.5, overflow: "visible" } }}
          >
            {body}
          </Popover>
        ) : (
          /**
           * NON-MODAL surface — everything else.
           *
           * No backdrop, which is a RENDERING choice and no longer a dismissal one: the
           * page underneath keeps its hover states and its scroll, while the pointerdown
           * handler above still eats the click that closes this editor (it used to reach
           * the next cell and open it — see `hooks/useOutsideDismiss.ts`). Safe only because none
           * of these controls render outside their own DOM subtree, which is why
           * `CellStatus` is a `MenuList` and the multi-selects are toggle grids rather than
           * `Select`s — a portalled listbox would fall outside `surfaceRef` and read as a
           * click away.
           */
          <Popper
            open
            anchorEl={anchorEl}
            placement="bottom-start"
            // ON the modal layer (1300), not below it. Above the sticky table header and the row
            // on /crm; and when the panel is itself hosted in a modal — `ContactPanelDrawer` over
            // the listing dialog sits at exactly 1300 — this popper is portalled AFTER it, so the
            // tie goes to the editor. At the old 1250 it opened invisibly BEHIND that drawer,
            // took the keystrokes, and ate the next click as "outside". A dialog opened later
            // still covers it (later portal wins); snackbars (1400) and tooltips (1500) stay on top.
            //
            // `style`, not `sx`: `Popper` in this MUI beta is an UNSTYLED utility — it has
            // no `styled()` wrapper, so `PopperProps` carries no `sx` and passing one is a
            // type error rather than a silently ignored prop. It does spread the rest onto
            // its root, so a plain inline style is the supported route. (Nothing here is
            // directional, so there is no stylis-rtl mirroring to worry about.)
            style={{ zIndex: theme.zIndex.modal }}
            modifiers={[{ name: "preventOverflow", options: { padding: 8 } }]}
          >
            {/* `ClickAwayListener` is now a BACKSTOP, not the mechanism. The pointerdown
                handler above closes this editor and eats the click that did it; this still
                runs if that path never fires (no pointer events at all), and `settledRef`
                makes the double harmless. */}
            <ClickAwayListener onClickAway={() => armed && dismiss()}>
              <Paper ref={surfaceRef} elevation={8} onBlur={handleBlur} sx={{ borderRadius: 1.5 }}>
                {body}
              </Paper>
            </ClickAwayListener>
          </Popper>
        ))}
    </>
  );
}

// ───────────────────────────────────────────────────────────────────────────────

/**
 * What the toast says when a cell save fails.
 *
 * ── The sentence is BORROWED, not written here ───────────────────────────────────
 *
 * `describeCrmBulkFailure(error, "edit")` is the CRM's established mapping from a server
 * envelope to Hebrew, and it is reused verbatim so a permission refusal reads identically
 * whether it came from a bulk edit or from one cell. It also replaces this file's previous
 * two-branch `status === 400` sniff, which was strictly worse: the CRM controller answers
 * 400 for a malformed patch, a busted cap AND an ambiguous org, so the status alone cannot
 * separate them, and the shared mapping keys on the server's own `code` and surfaces the
 * server's own reason — "at most 500 per bulk edit" is actionable, "השרת דחה את השמירה"
 * is not. Its drift and capped branches simply cannot fire for a single-contact write.
 *
 * The one case it does not cover is 404: a bulk edit reports a missing contact as a SKIP
 * rather than a failure, so there is no wording there to borrow, and "העדכון נכשל" would
 * leave the realtor retrying a document that no longer exists. The write hook reports that
 * 404 as `ContactNotInYourBookError` (its one translation, so every surface can tell "gone"
 * from "the network is down"); the raw server form is still tested for, because a failure
 * can reach this function from a retry closure built before a deploy.
 *
 * ── Why the field and the contact are named ─────────────────────────────────────
 *
 * This toast can land seconds after the editor closed, several rows further down, or on a
 * different page entirely (the snackbar host is above the router). "העדכון נכשל" with no
 * subject is then unactionable — the realtor cannot tell which of the six cells they just
 * filled came back. The label is the column header the CELL announced, not a mapping kept
 * in this file, so it is by construction the same word that was on screen above the value.
 *
 * The contact is named by NAME ONLY, never by falling back to `contactId`: that id is an
 * E.164 phone, and a bare `+9725…` dropped into a Hebrew sentence is a bidi island with no
 * `dir` attribute to protect it — the reordering rule this repo has already been bitten by.
 * A nameless contact simply goes unnamed.
 */
function describeCellSaveFailure(
  error: unknown,
  fieldLabel: string | null,
  contactName: string | null | undefined
): string {
  const isGone =
    isContactNotInYourBookError(error) ||
    (isWebotServerError(error) && (error.status === 404 || error.code === "CRM_NOT_FOUND"));
  const detail = isGone
      ? "הלקוח לא נמצא — ייתכן שנמחק. רעננו את הרשימה."
      : describeCrmBulkFailure(error, "edit").message;

  const who = contactName?.trim() || null;
  const subject = fieldLabel && who ? `${fieldLabel} · ${who}` : fieldLabel || who;

  return subject ? `${subject} — ${detail}` : detail;
}

export default CrmCellEditor;
